import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import updateSourceDataById from "@app/services/cases/updateSourceDataById";

import { useToast } from "@hooks/useToast";

import { CASES_DATA_STATUS_TYPE, DATA_SOURCE_TYPE } from "@utils/enum";

import {
  getCrimesDataTableConfig,
  getDismissedCrimesDataTableConfig,
  getIndictmentsDataTableConfig,
  getParsedCrimeData,
  prettifyProcessedData,
  prepareCrimeData,
} from "./CrimesTab.utils";

import { useDataSourceDetailsContext } from "../../providers";

export const useCrimesTab = () => {
  const { messages } = useIntl();
  const { dataSourceDetails } = useDataSourceDetailsContext();
  const {
    promptDialogData,
    handleClosePromptDialog,
    handleOpenPenaltyPrompt,
    handleOpenDescriptionPrompt,
    handleOpenExplanationPrompt,
  } = useCrimesTabPrompts();
  const {
    crimeToDelete,
    crimeToEdit,
    isAddCrimeDialogVisible,
    handleCloseDeleteCrimeDialog,
    handleCloseEditCrimeDialog,
    handleCloseAddCrimeDialog,
    handleOpenAddCrimeDialog,
    handleOpenDeleteCrimeDialog,
    handleOpenEditCrimeDialog,
  } = useCrimesTabSelectedCrimes();
  const {
    handleAddCrime,
    handleDeleteCrime,
    handleEditCrime,
    isCrimesLoading,
  } = useCrimesTabActions();

  const totalScore = dataSourceDetails?.data?.total_points ?? "";
  const sourceDataType = dataSourceDetails?.type ?? "";

  const crimesMap = useMemo(() => {
    const rawData = dataSourceDetails?.data;

    if (!rawData) {
      return {
        listOfCrimes: [],
        dismissedCrimes: [],
        indictments: [],
      };
    }

    return getParsedCrimeData(rawData);
  }, [dataSourceDetails?.data]);

  const isCrimesEditable = useMemo(() => {
    return dataSourceDetails?.status !== CASES_DATA_STATUS_TYPE.COMPLETED;
  }, [dataSourceDetails?.status]);

  const listOfCrimesConfig = useMemo(
    () =>
      getCrimesDataTableConfig(crimesMap.listOfCrimes, isCrimesEditable, {
        onViewPenalty: handleOpenPenaltyPrompt,
        onViewDescription: handleOpenDescriptionPrompt,
        onViewExplanation: handleOpenExplanationPrompt,
        onOpenDeleteCrimeDialog: handleOpenDeleteCrimeDialog,
        onOpenEditCrimeDialog: handleOpenEditCrimeDialog,
      }),
    [
      crimesMap.listOfCrimes,
      isCrimesEditable,
      handleOpenPenaltyPrompt,
      handleOpenDescriptionPrompt,
      handleOpenExplanationPrompt,
      handleOpenDeleteCrimeDialog,
      handleOpenEditCrimeDialog,
    ]
  );

  const dismissedCrimesConfig = useMemo(
    () =>
      getDismissedCrimesDataTableConfig(crimesMap.dismissedCrimes, {
        onViewExplanation: handleOpenExplanationPrompt,
      }),
    [crimesMap.dismissedCrimes, handleOpenExplanationPrompt]
  );

  const indictmentsConfig = useMemo(
    () =>
      getIndictmentsDataTableConfig(crimesMap.indictments, isCrimesEditable, {
        onViewExplanation: handleOpenExplanationPrompt,
        onOpenDeleteCrimeDialog: handleOpenDeleteCrimeDialog,
        onOpenEditCrimeDialog: handleOpenEditCrimeDialog,
      }),
    [
      crimesMap.indictments,
      isCrimesEditable,
      handleOpenExplanationPrompt,
      handleOpenDeleteCrimeDialog,
      handleOpenEditCrimeDialog,
    ]
  );

  const handleConfirmDeleteCrime = useCallback(() => {
    handleDeleteCrime(crimeToDelete);
    handleCloseDeleteCrimeDialog();
  }, [handleDeleteCrime, handleCloseDeleteCrimeDialog, crimeToDelete]);

  const handleConfirmEditCrime = useCallback(
    crimeData => {
      handleEditCrime(crimeData);
      handleCloseEditCrimeDialog();
    },
    [handleEditCrime, handleCloseEditCrimeDialog]
  );

  const handleConfirmAddCrime = useCallback(
    crimeData => {
      handleAddCrime(crimeData);
      handleCloseAddCrimeDialog();
    },
    [handleAddCrime, handleCloseAddCrimeDialog]
  );

  const crimesFormConfig = useMemo(() => {
    return {
      sourceDataType,
      title: crimeToEdit ? messages.label_edit_crime : messages.label_add_crime,
      data: crimeToEdit,
      onHide: crimeToEdit
        ? handleCloseEditCrimeDialog
        : handleCloseAddCrimeDialog,
      onSubmit: crimeToEdit ? handleConfirmEditCrime : handleConfirmAddCrime,
    };
  }, [
    sourceDataType,
    crimeToEdit,
    messages.label_edit_crime,
    messages.label_add_crime,
    handleCloseEditCrimeDialog,
    handleCloseAddCrimeDialog,
    handleConfirmEditCrime,
    handleConfirmAddCrime,
  ]);

  const isCrimesFormVisible = isAddCrimeDialogVisible || crimeToEdit !== null;

  const isIndictmentsVisible = useMemo(
    () => sourceDataType !== DATA_SOURCE_TYPE.ACTAPUBLICA_ROLE_ANALYSIS,
    [sourceDataType]
  );

  return {
    totalScore,
    sourceDataType,
    listOfCrimesConfig,
    dismissedCrimesConfig,
    indictmentsConfig,
    promptDialogData,
    crimeToDelete,
    crimeToEdit,
    isAddCrimeDialogVisible,
    handleClosePromptDialog,
    handleOpenAddCrimeDialog,
    handleCloseAddCrimeDialog,
    handleCloseDeleteCrimeDialog,
    handleCloseEditCrimeDialog,
    handleConfirmDeleteCrime,
    crimesFormConfig,
    isCrimesFormVisible,
    isCrimesEditable,
    isIndictmentsVisible,
    isCrimesLoading,
  };
};

export const useCrimesTabPrompts = () => {
  const { messages } = useIntl();

  const [promptDialogData, setPromptDialogData] = useState(null);

  const handleClosePromptDialog = useCallback(() => {
    setPromptDialogData(null);
  }, []);

  const handleOpenPromptDialog = useCallback((title, message) => {
    setPromptDialogData({
      title,
      message,
    });
  }, []);

  const handleOpenPenaltyPrompt = useCallback(
    penaltyMessage => {
      handleOpenPromptDialog(messages.message_penalty, penaltyMessage);
    },
    [handleOpenPromptDialog, messages.message_penalty]
  );

  const handleOpenDescriptionPrompt = useCallback(
    descriptionMessage => {
      handleOpenPromptDialog(
        messages.title_crime_description,
        descriptionMessage
      );
    },
    [handleOpenPromptDialog, messages.title_crime_description]
  );

  const handleOpenExplanationPrompt = useCallback(
    explanationMessage => {
      handleOpenPromptDialog(messages.title_ai_explanation, explanationMessage);
    },
    [handleOpenPromptDialog, messages.title_ai_explanation]
  );

  return {
    promptDialogData,
    handleClosePromptDialog,
    handleOpenPenaltyPrompt,
    handleOpenDescriptionPrompt,
    handleOpenExplanationPrompt,
  };
};

export const useCrimesTabActions = () => {
  const { dataSourceDetails, setDataSourceDetails } =
    useDataSourceDetailsContext();
  const { messages } = useIntl();
  const { case_id, data_source_id } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const rawData = useMemo(
    () => dataSourceDetails?.data ?? {},
    [dataSourceDetails?.data]
  );

  const handleUpdateSourceData = useCallback(
    async nextValues => {
      const payload = {
        processed_data: nextValues,
      };

      setIsLoading(true);

      try {
        const response = await updateSourceDataById(
          case_id,
          data_source_id,
          payload
        );

        setDataSourceDetails(response);

        showSuccessToast(messages.text_update_successful);
      } catch {
        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    },
    [
      case_id,
      data_source_id,
      messages.exception_error_message,
      messages.text_update_successful,
      setDataSourceDetails,
      showErrorToast,
      showSuccessToast,
    ]
  );

  const handleDeleteCrime = useCallback(
    async ({ hash }) => {
      const prettifiedProcessedData = prettifyProcessedData(rawData);
      const updatedProcessedData = prettifiedProcessedData.map(item => {
        const nextItem = { ...item };

        if (nextItem.hash === hash) {
          nextItem.isDeleted = true;
        }

        delete nextItem.hash;

        return nextItem;
      });

      await handleUpdateSourceData(updatedProcessedData);
    },
    [handleUpdateSourceData, rawData]
  );

  const handleEditCrime = useCallback(
    async editedCrime => {
      const formattedEditedCrime = prepareCrimeData(editedCrime);
      const prettifiedProcessedData = prettifyProcessedData(rawData);
      const updatedProcessedData = prettifiedProcessedData.map(item => {
        let nextItem = { ...item };

        if (nextItem.hash === formattedEditedCrime.hash) {
          nextItem = { ...nextItem, ...formattedEditedCrime, isChanged: true };
        }

        delete nextItem.hash;

        return nextItem;
      });

      await handleUpdateSourceData(updatedProcessedData);
    },
    [handleUpdateSourceData, rawData]
  );

  const handleAddCrime = useCallback(
    async crimeToAdd => {
      const formattedCrimeToAdd = {
        ...prepareCrimeData(crimeToAdd),
        isAdded: true,
      };

      delete formattedCrimeToAdd.hash;

      const updatedProcessedData =
        rawData.processed_data.concat(formattedCrimeToAdd);

      await handleUpdateSourceData(updatedProcessedData);
    },
    [handleUpdateSourceData, rawData]
  );

  return {
    handleDeleteCrime,
    handleEditCrime,
    handleAddCrime,
    isCrimesLoading: isLoading,
  };
};

export const useCrimesTabSelectedCrimes = () => {
  const [crimeToDelete, setCrimeToDelete] = useState(null);
  const [crimeToEdit, setCrimeToEdit] = useState(null);
  const [isAddCrimeDialogVisible, setIsAddCrimeDialogVisible] = useState(false);

  const handleCloseDeleteCrimeDialog = useCallback(() => {
    setCrimeToDelete(null);
  }, []);

  const handleCloseEditCrimeDialog = useCallback(() => {
    setCrimeToEdit(null);
  }, []);

  const handleCloseAddCrimeDialog = useCallback(() => {
    setIsAddCrimeDialogVisible(false);
  }, []);

  const handleOpenDeleteCrimeDialog = useCallback(crime => {
    setCrimeToDelete(crime);
  }, []);

  const handleOpenEditCrimeDialog = useCallback(crime => {
    setCrimeToEdit(crime);
  }, []);

  const handleOpenAddCrimeDialog = useCallback(() => {
    setIsAddCrimeDialogVisible(true);
  }, []);

  return {
    crimeToDelete,
    crimeToEdit,
    isAddCrimeDialogVisible,
    handleCloseDeleteCrimeDialog,
    handleCloseEditCrimeDialog,
    handleCloseAddCrimeDialog,
    handleOpenDeleteCrimeDialog,
    handleOpenEditCrimeDialog,
    handleOpenAddCrimeDialog,
  };
};
